'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(() => {
      // Network error — treat as unauthenticated
      setSession(null);
      setUser(null);
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up (kept for compatibility)
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In (kept for compatibility)
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get Member profile from the member table (linked via auth_user_id)
  const getMemberProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('member')
      .select('*, office:office_id(*, company:company_id(*))')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  // Get member roles — A3 fix: resolve member from auth_user_id first, then load roles
  const getMemberRoles = async () => {
    if (!user) return [];

    // Step 1: resolve the member row by auth_user_id
    const { data: memberRow, error: memberError } = await supabase
      .from('member')
      .select('id, status')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    if (memberError || !memberRow) return [];

    // Only active members have valid roles
    if (memberRow.status !== 'active') return [];

    // Step 2: load roles using the member table PK (member_id), not auth user.id
    const { data, error } = await supabase
      .from('member_role')
      .select('*')
      .eq('member_id', memberRow.id);

    if (error) return [];
    return data || [];
  };

  // Check if current user is platform admin or super admin
  const isAdmin = async () => {
    if (!user) return false;

    // Resolve member row first
    const { data: memberRow } = await supabase
      .from('member')
      .select('id, status')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    if (!memberRow || memberRow.status !== 'active') return false;

    const { data } = await supabase
      .from('member_role')
      .select('role')
      .eq('member_id', memberRow.id)
      .in('role', ['platform_admin', 'super_admin'])
      .limit(1);

    return (data?.length ?? 0) > 0;
  };

  // Legacy: Get User Profile from Database (kept for compatibility)
  const getUserProfile = async () => {
    return getMemberProfile();
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    getMemberProfile,
    getMemberRoles,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
